import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Customer } from '../mocks/crm';

function rowToCustomer(r: Record<string, unknown>): Customer {
  const name = (r.full_name as string) || '';
  return {
    id: r.id as string,
    name,
    email: (r.email as string) || '',
    phone: (r.phone as string) || '',
    nationality: (r.nationality as string) || '',
    passportNo: (r.passport_number as string) || '',
    passportExpiry: '',
    dob: (r.date_of_birth as string) || '',
    address: [(r.address as string), (r.city as string), (r.country as string)].filter(Boolean).join(', '),
    avatar: name.slice(0, 2).toUpperCase() || 'CU',
    totalBookings: (r.total_bookings as number) || 0,
    totalSpent: `AED ${((r.total_spent as number) || 0).toLocaleString()}`,
    lastBooking: '',
    joinedAt: (r.created_at as string) || '',
    tags: (r.tags as string[]) || [],
    bookings: [],
    notes: [],
  };
}

export function useCustomers() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('customers').select('*').order('created_at', { ascending: false });
    setCustomers((data || []).map((r) => rowToCustomer(r as Record<string, unknown>)));
    setLoading(false);
  }, []);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const createCustomer = async (customer: Partial<Customer>) => {
    const { data, error } = await supabase.from('customers').insert({
      full_name: customer.name,
      email: customer.email,
      phone: customer.phone,
      nationality: customer.nationality,
      passport_number: customer.passportNo,
      date_of_birth: customer.dob || null,
      address: customer.address,
      tags: customer.tags || [],
    }).select().maybeSingle();
    if (!error && data) setCustomers((prev) => [rowToCustomer(data as Record<string, unknown>), ...prev]);
    return { data, error };
  };

  const deleteCustomer = async (id: string) => {
    const { error } = await supabase.from('customers').delete().eq('id', id);
    if (!error) setCustomers((prev) => prev.filter((c) => c.id !== id));
  };

  return { customers, loading, refetch: fetchCustomers, createCustomer, deleteCustomer };
}
